import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import NetworkVisual from '../components/visuals/NetworkVisual';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "Introduction",
      content: "Welcome to PR Boost. We are committed to protecting your privacy. This Privacy Policy outlines how we collect, use, disclose, and protect your personal information when you use our website and services. By accessing our website and using our services, you consent to the collection and use of your personal information as described in this policy."
    },
    {
      title: "Information We Collect",
      subsections: [
        {
          subtitle: "Personal Information",
          text: "We may collect personal information that you provide to us, including but not limited to: Name, Email address, Phone number, Billing and mailing address, and Payment information."
        },
        {
          subtitle: "Non-Personal Information",
          text: "We may also collect non-personal information about your visit to our website, including: Browser type, IP address, Pages visited, and Time and date of visit."
        }
      ]
    },
    {
      title: "How We Use Your Information",
      subsections: [
        {
          subtitle: "Personal Information",
          text: "We use your personal information to: Provide and manage our services, process your transactions, communicate with you regarding your account and services, send promotional materials and newsletters (you may opt out at any time), and improve our website and services."
        },
        {
          subtitle: "Non-Personal Information",
          text: "We use non-personal information to: Analyze and improve the performance and usability of our website, and monitor and analyze trends and usage patterns."
        }
      ]
    },
    {
      title: "Disclosure of Your Information",
      content: "We do not sell, trade, or otherwise transfer your personal information to outside parties except as described below:",
      list: [
        { title: "Service Providers", text: "We may share your information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you, provided that those parties agree to keep this information confidential." },
        { title: "Legal Requirements", text: "We may disclose your information if required to do so by law or in response to valid requests by public authorities." }
      ]
    },
    {
      title: "Security",
      content: "We implement a variety of security measures to protect your personal information. However, no method of transmission over the internet or method of electronic storage is 100% secure, and we cannot guarantee absolute security."
    },
    {
      title: "Cookies",
      content: "Our website uses cookies to enhance your experience. Cookies are small files that a site or its service provider transfers to your computer's hard drive through your web browser that enables the site's or service provider's systems to recognize your browser and capture and remember certain information. You can choose to disable cookies through your individual browser options. However, disabling cookies may affect your ability to use certain features of our website."
    },
    {
      title: "Third-Party Links",
      content: "Our website may contain links to third-party sites. These third-party sites have separate and independent privacy policies. We have no responsibility or liability for the content and activities of these linked sites."
    },
    {
      title: "Children's Privacy",
      content: "Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that a child under 18 has provided us with personal information, we will take steps to delete such information."
    },
    {
      title: "Changes to This Privacy Policy",
      content: "PR Boost reserves the right to update or change this Privacy Policy at any time. Any changes will be posted on this page, and your continued use of our website and services constitutes acceptance of the updated Privacy Policy."
    }
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Header Section */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wider mb-6 border border-blue-500/20"
            >
              <SafeIcon icon={FiIcons.FiShield} /> Legal Documentation
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 tracking-tight"
            >
              Privacy <span className="text-blue-500 text-gradient">Policy</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-400 font-medium"
            >
              Last updated: {new Date().toLocaleDateString()}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="glass-card p-8 md:p-16 rounded-[2.5rem] border-white/5 bg-[#12151e]/80 backdrop-blur-xl shadow-2xl">
            <div className="space-y-16">
              {sections.map((section, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="group"
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-4">
                    <span className="text-blue-500/80 text-4xl font-mono group-hover:text-blue-400 transition-colors">
                      {(idx + 1).toString().padStart(2, '0')}
                    </span>
                    {section.title}
                  </h2>
                  
                  {section.content && (
                    <p className="text-slate-400 text-lg leading-relaxed mb-6">
                      {section.content}
                    </p>
                  )}

                  {section.subsections && (
                    <div className="grid gap-8 mt-8">
                      {section.subsections.map((sub, sIdx) => (
                        <div key={sIdx} className="bg-white/5 p-6 rounded-2xl border border-white/5">
                          <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                            {sub.subtitle}
                          </h4>
                          <p className="text-slate-400 leading-relaxed">
                            {sub.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {section.list && (
                    <div className="space-y-6 mt-6">
                      {section.list.map((item, lIdx) => (
                        <div key={lIdx} className="flex gap-4">
                          <div className="mt-1.5 text-blue-500">
                            <SafeIcon icon={FiIcons.FiCheckCircle} />
                          </div>
                          <div>
                            <span className="text-white font-bold">{item.title}: </span>
                            <span className="text-slate-400 leading-relaxed">{item.text}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Contact Us Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="pt-12 border-t border-white/5"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">Contact Us</h2>
                <div className="bg-blue-500/5 border border-blue-500/20 p-8 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center gap-6">
                    <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                      <SafeIcon icon={FiIcons.FiMail} className="text-3xl" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-xl">PR Boost</h4>
                      <p className="text-blue-400 font-medium">support@prboost.io</p>
                    </div>
                  </div>
                  <div className="text-center md:text-right">
                    <p className="text-slate-400 text-sm max-w-[200px]">
                      Reach out to our legal team for any privacy concerns.
                    </p>
                  </div>
                </div>
              </motion.div>

              <p className="text-center text-slate-500 text-sm italic pt-12">
                By using our website and services, you acknowledge that you have read, understood, and agree to the terms of this Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;